import { Receipt, Zap, Activity, SunDim, Sun, Wallet, Gauge, HelpCircle, Wheat, Soup, Factory, Warehouse, Snowflake, Cross, Building, Building2, MoreHorizontal, IndianRupee, MapPin } from "lucide-react";

export const track = (event, props = {}) => {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...props });
    if (typeof window.gtag === "function") window.gtag("event", event, props);
    if (typeof window.plausible === "function") window.plausible(event, { props });
  } catch { /* analytics must never break the UI */ }
};

export const PRODUCT_META = {
  "energyscan": { name: "EnergyScan", to: "/products/energyscan" },
  "guard": { name: "WattZap Guard", to: "/products/guard" },
  "pf-guard": { name: "PF Guard", to: "/products/guard/pf-guard" },
  "demand-guard": { name: "Demand Guard", to: "/products/guard/demand-guard" },
  "bill-guard": { name: "Bill Guard", to: "/products/guard/bill-guard" },
  "solar-guard": { name: "Solar Guard", to: "/products/guard/solar-guard" },
  "ems-lite": { name: "EMS Lite", to: "/products/ems-lite" },
  "solarfit": { name: "SolarFit", to: "/products/solarfit" },
  "solarlease": { name: "SolarLease", to: "/products/solarlease" },
  "savings-assurance": { name: "Savings Assurance", to: "/products/savings-assurance" },
};

export const QUESTIONS = [
  {
    id: "industry", multi: false,
    step: "Step 1 of 4 — Your facility",
    ask: "What type of facility do you operate?",
    options: [
      { id: "rice", label: "Rice mill", icon: Wheat },
      { id: "food", label: "Food-processing facility", icon: Soup },
      { id: "mfg", label: "Manufacturing plant", icon: Factory },
      { id: "warehouse", label: "Warehouse", icon: Warehouse },
      { id: "cold", label: "Cold storage", icon: Snowflake },
      { id: "hospital", label: "Hospital", icon: Cross },
      { id: "hotel", label: "Hotel", icon: Building },
      { id: "commercial", label: "Commercial building", icon: Building2 },
      { id: "other", label: "Other", icon: MoreHorizontal },
    ],
  },
  {
    id: "bill", multi: false,
    step: "Step 2 of 4 — Your electricity spend",
    ask: "Approximately how much is your monthly electricity bill?",
    hint: "Used only to route you correctly — kept in this session only, never stored or used for savings claims.",
    options: [
      { id: "b0", label: "Below ₹1 lakh", icon: IndianRupee },
      { id: "b1", label: "₹1–5 lakh", icon: IndianRupee },
      { id: "b2", label: "₹5–15 lakh", icon: IndianRupee },
      { id: "b3", label: "₹15–50 lakh", icon: IndianRupee },
      { id: "b4", label: "Above ₹50 lakh", icon: IndianRupee },
      { id: "bx", label: "I am not sure", icon: HelpCircle },
    ],
  },
  {
    id: "problem", multi: true,
    step: "Step 3 of 4 — What to investigate",
    ask: "What would you like WattZap to investigate?",
    hint: "Select all that apply.",
    options: [
      { id: "bill-high", label: "My electricity bill is too high", icon: Receipt },
      { id: "pf", label: "I am paying power-factor or other penalties", icon: Zap },
      { id: "demand", label: "My maximum-demand charges are high", icon: Activity },
      { id: "solar-under", label: "My solar system is not performing well", icon: SunDim },
      { id: "want-solar", label: "I want to install solar", icon: Sun },
      { id: "solar-lease", label: "I want solar without heavy upfront investment", icon: Wallet },
      { id: "waste", label: "I want to reduce energy waste", icon: Gauge },
      { id: "unknown", label: "I do not know what the problem is", icon: HelpCircle },
    ],
  },
  {
    id: "state", multi: false,
    step: "Step 4 of 4 — Your state & DISCOM",
    ask: "Which state is your facility in?",
    hint: "This helps map your DISCOM tariff structure.",
    options: [
      { id: "ts", label: "Telangana (TGSPDCL / TGNPDCL)", icon: MapPin },
      { id: "ap", label: "Andhra Pradesh (APSPDCL / APEPDCL / APCPDCL)", icon: MapPin },
      { id: "other-state", label: "Another Indian state", icon: MapPin },
    ],
  },
];

export const INDUSTRY_ACK = {
  rice: "Rice mills can lose money through motor operation, power-factor penalties, demand peaks and inefficient operating schedules. Let's narrow down your opportunity.",
  food: "Food-processing facilities commonly see refrigeration, batch heating and demand peaks drive avoidable costs. Let's narrow it down.",
  mfg: "Manufacturing plants may lose money through motor loads, poor start sequencing and power-factor exposure. Let's narrow it down.",
  warehouse: "Warehouses can carry avoidable costs in lighting, ventilation and idle equipment — and often have good solar roofs. Let's continue.",
  cold: "Cold storage commonly runs compressors around the clock, so demand behaviour and operating schedules can matter a lot. Let's continue.",
  hospital: "Hospitals may have continuous critical loads where power quality, demand and HVAC scheduling can affect the bill. Let's continue.",
  hotel: "Hotels can see costs driven by HVAC, hot water and occupancy patterns — often with solar potential. Let's continue.",
  commercial: "Commercial buildings commonly have HVAC-driven peaks and time-of-day effects worth examining. Let's continue.",
  other: "That helps. Most facilities share the same cost drivers — penalties, demand peaks and operating schedules. Let's continue.",
};

const AREAS = {
  "bill-high": ["Avoidable energy consumption", "Billing anomalies", "Equipment operating schedules"],
  "pf": ["Power-factor penalties", "Billing anomalies"],
  "demand": ["Maximum-demand peaks", "Equipment operating schedules"],
  "solar-under": ["Solar-system performance", "Billing anomalies"],
  "want-solar": ["Solar-sizing opportunity", "Avoidable energy consumption"],
  "solar-lease": ["Solar-sizing opportunity"],
  "waste": ["Equipment operating schedules", "Avoidable energy consumption", "Maximum-demand peaks"],
  "unknown": ["Billing anomalies", "Power-factor penalties", "Maximum-demand peaks", "Avoidable energy consumption"],
};

const ROUTES = {
  A: { message: "The best starting point is to understand where the money is being lost. WattZap EnergyScan analyses electricity bills, penalties, demand patterns, operating behaviour and possible energy-saving opportunities.", products: ["energyscan", "guard", "ems-lite", "solarfit"] },
  B: { message: "EnergyScan identifies the source of losses before any optimisation or investment decision is made.", products: ["energyscan", "ems-lite", "solarfit"] },
  C: { message: "PF Guard helps identify and reduce avoidable power-factor penalty exposure — after EnergyScan confirms the cause.", products: ["energyscan", "pf-guard", "guard"] },
  D: { message: "Demand Guard helps monitor and manage expensive demand peaks before they reach the bill.", products: ["demand-guard", "energyscan", "ems-lite"] },
  E: { message: "Solar Guard helps identify whether your solar installation is producing and delivering value as expected.", products: ["solar-guard", "energyscan", "savings-assurance"] },
  F: { message: "SolarFit sizes and designs solar based on your actual consumption and operating behaviour — not just the roof area.", products: ["solarfit", "ems-lite", "savings-assurance"] },
  G: { message: "SolarLease lets suitable customers access solar savings with reduced upfront investment — WattZap funds, owns and operates the plant. Site-specific; subject to technical and credit assessment.", products: ["solarlease", "solarfit", "savings-assurance"] },
  H: { message: "EMS Lite helps optimise operating schedules and controllable energy usage — EnergyScan first confirms where the waste is.", products: ["energyscan", "ems-lite", "demand-guard"] },
  I: { message: "With several concerns, the most reliable route is the full WattZap path: Diagnose → Protect → Optimise → Generate → Finance → Verify.", products: ["energyscan", "guard", "ems-lite", "solarfit", "savings-assurance"] },
};

const PROBLEM_ROUTE = { "unknown": "A", "bill-high": "B", "pf": "C", "demand": "D", "solar-under": "E", "want-solar": "F", "solar-lease": "G", "waste": "H" };

export function recommend(answers) {
  const problems = answers.problem || [];
  const systems = answers.systems || [];
  let routeId;
  if (problems.length > 1) routeId = "I";
  else routeId = PROBLEM_ROUTE[problems[0]] || "A";
  const route = ROUTES[routeId];
  let products = [...route.products];
  if (routeId === "B" && systems.includes("solar")) {
    products = products.map((p) => (p === "solarfit" ? "solar-guard" : p));
  }
  if (routeId === "I" && problems.includes("solar-lease") && !products.includes("solarlease")) {
    products.splice(4, 0, "solarlease");
  }
  const areas = [...new Set(problems.flatMap((p) => AREAS[p] || []))].slice(0, 4);
  return { routeId, message: route.message, products, primary: products[0], areas: areas.length ? areas : AREAS["unknown"].slice(0, 3) };
}
